import React from 'react';
import { StarIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { DisclosureBadgeGroup } from '../../components/Disclosure/DisclosureBadgeGroup';
import type { DisclosureId } from '../../data/disclosures';
import type { BookBox } from './types';
interface ReviewsTabProps {
  bookBox: BookBox;
}
export const ReviewsTab: React.FC<ReviewsTabProps> = ({
  bookBox
}) => {
  const {
    darkMode
  } = useDarkMode();
  return <div data-id="element-6943" data-mp="3aj7ac9yrameq">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-6944" data-mp="3aiyy8kagjl4l">
        <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6945" data-mp="36m2v0gz8r5xp">
          Customer Reviews
        </h2>
        <button className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors" data-id="element-6946" data-mp="2wmkde76szw8t">
          Write a Review
        </button>
      </div>

      {bookBox.ratings && <div className={`border rounded-lg p-6 mb-8 shadow-sm ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-6947" data-mp="uvh0tcdcs7lm">
          <div className="flex flex-col md:flex-row md:items-center gap-6" data-id="element-6948" data-mp="3dnx3566evyqm">
            <div className="text-center" data-id="element-6949" data-mp="3dlu27r3q0l74">
              <div className={`text-5xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6950" data-mp="2zp76tsrf233b">
                {bookBox.ratings.average}
              </div>
              <div className="flex justify-center text-yellow-400 mb-1" data-id="element-6951" data-mp="3sct7as5ckrly">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-6 w-6 ${i < Math.floor(bookBox.ratings.average) ? 'fill-current' : 'stroke-current fill-transparent'}`} data-id="element-6952" data-mp="15gqwfcagi73" data-mpi={i} />)}
              </div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6953" data-mp="f6jewxmug2kn">
                {bookBox.ratings.count} reviews
              </p>
            </div>
            <div className="flex-1" data-id="element-6954" data-mp="tszxmwhuqqak">
              <div className="space-y-2" data-id="element-6955" data-mp="ts6vy9xr2qza">
                {[5, 4, 3, 2, 1].map(rating => {
              const percentage = rating === 5 ? 70 : rating === 4 ? 20 : rating === 3 ? 7 : rating === 2 ? 2 : 1;
              return <div key={rating} className="flex items-center" data-id="element-6956" data-mp="63xjhymfffkt">
                      <div className="flex items-center w-28" data-id="element-6957" data-mp="61kqmqf4rksz">
                        <span className={`text-sm mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6958" data-mp="15m5rct0gkxb">
                          {rating} stars
                        </span>
                      </div>
                      <div className={`flex-1 h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-6959" data-mp="3ja7bhs10q2ll">
                        <div className="h-full bg-yellow-400 rounded-full" style={{
                    width: `${percentage}%`
                  }} data-id="element-6960" data-mp="1yu99iadgiivw" />
                      </div>
                      <span className={`text-sm ml-2 w-12 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6961" data-mp="2cqms5mtq8qby">
                        {percentage}%
                      </span>
                    </div>;
            })}
              </div>
            </div>
          </div>
        </div>}

      {/* Sample Reviews */}
      <div className="space-y-6" data-id="element-6962" data-mp="1ttbw0b3e6d0q">
        {[{
        initials: 'SK',
        name: 'Sarah K.',
        meta: 'Verified Subscriber · 3 months ago',
        disclosures: ['verified-purchase'] as DisclosureId[],
        stars: 5,
        title: 'Amazing monthly surprise!',
        body: "I've been subscribed for 6 months now and every box has been thoughtfully curated. The books are always ones I wouldn't have picked myself but end up loving! The quality of the included items is excellent, and I love how everything matches the theme perfectly.",
        avatarColor: darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-indigo-100 text-indigo-700'
      }, {
        initials: 'MT',
        name: 'Michael T.',
        meta: 'Verified Subscriber · 1 month ago',
        // A gifted box that still received a critical note on shipping —
        // disclosure describes the relationship, not the sentiment.
        disclosures: ['gifted-product'] as DisclosureId[],
        stars: 4,
        title: 'Great selection, shipping could be faster',
        body: "Love the curation and the quality of items. My only complaint is that shipping can be slow sometimes, but the wait is worth it! Each box feels like it was designed just for me, and I've discovered some amazing new authors.",
        avatarColor: darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
      }].map((review, idx) => <div key={idx} className={`border rounded-lg p-6 shadow-sm ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-6963" data-mp="1ifsybmvhd34y" data-mpi={idx}>
            <div className="flex items-center mb-4" data-id="element-6964" data-mp="18i520si87nko">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center font-medium mr-4 ${review.avatarColor}`} data-id="element-6965" data-mp="18iy4hha5jqx0">
                {review.initials}
              </div>
              <div className="min-w-0" data-id="element-6966" data-mp="zhqh3u96cahp">
                <div className="flex flex-wrap items-center gap-2" data-id="element-6967" data-mp="2owo86mxd0d64">
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6968" data-mp="2oubqmavik4tl">
                    {review.name}
                  </h4>
                  {/* Commercial disclosure for a bookish product */}
                  <DisclosureBadgeGroup disclosures={review.disclosures} subjectType="product" data-id="element-6969" data-mp="2ne69x5rrluyo" />
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-6970" data-mp="2dhl62nrhepwf">
                  {review.meta}
                </p>
              </div>
            </div>
            <div className="flex text-yellow-400 mb-3" data-id="element-6971" data-mp="365h0tmxhq7qk">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-5 w-5 ${i < review.stars ? 'fill-current' : 'stroke-current fill-transparent'}`} data-id="element-6972" data-mp="31dyzuyvgw75l" data-mpi={i} />)}
            </div>
            <h5 className={`font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6973" data-mp="3tw3656g41dwv">
              {review.title}
            </h5>
            <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6974" data-mp="av8oauoilkg">
              {review.body}
            </p>
            <div className={`flex items-center text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-6975" data-mp="5bizowiwr2eo">
              <button className={`font-medium ${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-indigo-600 hover:text-indigo-800'}`} data-id="element-6976" data-mp="f7cnxd5zokca">
                Helpful
              </button>
              <span className="mx-2" data-id="element-6977" data-mp="o793mpaei8v7">·</span>
              <button className={`${darkMode ? 'hover:text-gray-300' : 'hover:text-gray-700'}`} data-id="element-6978" data-mp="o67r0gdqlebd">
                Report
              </button>
            </div>
          </div>)}
      </div>

      <div className="mt-6 text-center" data-id="element-6979" data-mp="12xgpzg3j4c16">
        <button className={`font-medium ${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-indigo-600 hover:text-indigo-800'}`} data-id="element-6980" data-mp="12yi3rsbxjcml">
          Load more reviews
        </button>
      </div>
    </div>;
};